import { Paper } from "@mui/material";
import FlashingCardHeader from "./FlashingCardHeader";
import FlashingCardBody from "./FlashingCardBody";
import FlashingCardFooter from "./FlashingCardFooter";
import { useState } from "react";
import convertToArabicNumbers from "utils/convertToArabicNumbers";

/**
 * Represents a card displaying detailed information with a header, body, and footer.
 * Each card supports additional dialogs for interaction and displays.
 *
 * @component
 * @param {Object} props  The component props.
 * @param {Object} props.card  The card data.
 * @param {string} props.card.id  Unique identifier of the card.
 * @param {string} props.card.orderId  Order ID.
 * @param {string} props.card.rowId  Row ID for special cases  driver change requests.
 * @param {string} props.card.orderNumber  Order number displayed in the header.
 * @param {string} props.card.status  Status of the card (e.g., Pending, Resolved).
 * @param {string} props.card.statusColor  Color representing the card's status.
 * @param {string} props.card.senderName  Name of the sender.
 * @param {string} props.card.receiverName  Name of the receiver.
 * @param {string} props.card.cycleTime  Elapsed time since the issue was modified.
 * @param {string} props.card.scheduledTime  Scheduled time for the order or issue.
 * @param {string} props.card.actualTime  Actual time logged for the issue or order.
 * @param {string} props.card.driverName  Name of the driver associated with the issue.
 * @param {string} props.card.reason  Reason associated with the issue.
 * @param {string} props.card.driverPhoneNumber  Driver's contact number.
 * @param {string} props.card.senderPhoneNumber  Sender's contact number.
 * @param {string} props.card.receiverPhoneNumber  Receiver's contact number.
 */
function FlashingCard({ card }) {
  const [footerDialogOpen, setFooterDialogOpen] = useState(false);
  const [bodyDialogOpen, setBodyDialogOpen] = useState(false);

  const handleFooterDialogOpen = () => setFooterDialogOpen(true);
  const handleFooterDialogClose = () => setFooterDialogOpen(false);

  const handleBodyDialogOpen = () => setBodyDialogOpen(true);
  const handleBodyDialogClose = () => setBodyDialogOpen(false);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: "background.white",
        borderLeft: `8px solid ${card.statusColor}`,
        borderRadius: "10px",
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        boxShadow:
          "0px 4px 6px rgba(0, 0, 0, 0.3), 0px -2px 4px rgba(0, 0, 0, 0.2)",
      }}
    >
      <FlashingCardHeader
        orderId={card.orderId}
        rowId={card.rowId}
        orderNumber={card.orderNumber}
        status={card.status}
        statusColor={card.statusColor}
        convertToArabicNumbers={convertToArabicNumbers}
      />

      <FlashingCardBody
        senderName={card.senderName}
        receiverName={card.receiverName}
        cycleTime={card.cycleTime}
        scheduledTime={card.scheduledTime}
        actualTime={card.actualTime}
        driverName={card.driverName}
        reason={card.reason}
        driverPhoneNumber={card.driverPhoneNumber}
        dialogOpen={bodyDialogOpen}
        handleDialogOpen={handleBodyDialogOpen}
        handleDialogClose={handleBodyDialogClose}
      />

      <FlashingCardFooter
        dialogOpen={footerDialogOpen}
        handleDialogOpen={handleFooterDialogOpen}
        handleDialogClose={handleFooterDialogClose}
        driverPhoneNumber={card.driverPhoneNumber}
        senderPhoneNumber={card.senderPhoneNumber}
        receiverPhoneNumber={card.receiverPhoneNumber}
        issueId={card.id}
      />
    </Paper>
  );
}

export default FlashingCard;
